<template>
  <div class="mobile-user-center" :class="{ 'dark-theme': isDark }">
    <!-- 顶部导航 -->
    <header class="mobile-header">
      <button @click="goBack" class="back-btn">
        <i class="fas fa-arrow-left"></i>
      </button>
      <h1>用户中心</h1>
      <div class="header-actions">
        <button @click="openSettings" class="settings-btn">
          <i class="fas fa-cog"></i>
        </button>
        <!-- 主题切换按钮 -->
        <div class="theme-toggle" @click="toggleTheme">
          <i :class="isDark ? 'fas fa-sun' : 'fas fa-moon'"></i>
        </div>
      </div>
    </header>

    <!-- 主要内容 -->
    <main class="main-content">
      <!-- 用户信息卡片 -->
      <div class="user-info-card">
        <div class="user-avatar">
          <img 
            :src="userInfo.avatar_url || 'https://picsum.photos/120/120?random=1'" 
            :alt="userInfo.username"
            @click="showEditProfile = true"
          />
          <div class="avatar-edit-btn" @click="showEditProfile = true">
            <i class="fas fa-camera"></i>
          </div>
        </div>
        <div class="user-details">
          <h2>{{ userInfo.username || '用户' }}</h2>
          <p>{{ userInfo.company_name || '暂无公司信息' }}</p>
          <div class="user-stats">
            <div class="stat-item">
              <i class="fas fa-phone"></i>
              <span>{{ userInfo.phone || '未绑定' }}</span>
            </div>
            <div class="stat-item">
              <i class="fas fa-envelope"></i>
              <span>{{ userInfo.email || '未绑定' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 会员状态卡片 -->
      <div class="membership-card">
        <div class="card-header">
          <h3>
            <i class="fas fa-crown"></i>
            会员状态
          </h3>
          <div class="header-actions">
            <button v-if="membershipInfo" @click="handleRefreshRemaining" :disabled="refreshing" class="refresh-btn">
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': refreshing }"></i>
              {{ refreshing ? '刷新中...' : '刷新额度' }}
            </button>
            <button v-if="!membershipInfo" @click="goToPackageCenter" class="upgrade-btn">
              升级
            </button>
          </div>
        </div>
        
        <div v-if="membershipInfo" class="membership-info">
          <div class="membership-badge">
            <i class="fas fa-crown"></i>
            <span>{{ membershipInfo.membership_name }}</span>
          </div>
          <div class="membership-details">
            <div class="detail-row">
              <span class="label">每日可获得:</span>
              <span class="value">{{ membershipInfo.daily_limit }} 次</span>
            </div>
            <div class="detail-row">
              <span class="label">剩余次数:</span>
              <span class="value">{{ getRemainingCalls(membershipInfo) }} 次</span>
            </div>
            <div class="detail-row">
              <span class="label">今日已用:</span>
              <span class="value">{{ membershipInfo.today_calls }} 次</span>
            </div>
            <div class="detail-row" v-if="membershipInfo.remaining_calls_last_refresh">
              <span class="label">最后刷新:</span>
              <span class="value">{{ formatDate(membershipInfo.remaining_calls_last_refresh) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">到期时间:</span>
              <span class="value">{{ formatDate(membershipInfo.expires_at) }}</span>
            </div>
          </div>
          <!-- 使用进度条 -->
          <div class="usage-progress">
            <div class="progress-info">
              <span>剩余额度</span>
              <span>{{ getRemainingCalls(membershipInfo) }} 次</span>
            </div>
            <div class="progress-bar">
              <div 
                class="progress-fill"
                :style="{ width: Math.min((getRemainingCalls(membershipInfo) / Math.max(membershipInfo.daily_limit, 1)) * 100, 100) + '%' }"
              ></div>
            </div>
            <div class="progress-hint" v-if="membershipInfo.remaining_calls_last_refresh">
              <span>未使用的额度会累积，最后刷新：{{ formatDate(membershipInfo.remaining_calls_last_refresh) }}</span>
            </div>
          </div>
        </div>
        
        <div v-else class="no-membership">
          <div class="empty-icon">
            <i class="fas fa-user-clock"></i>
          </div>
          <p>您还没有激活会员</p>
          <button @click="goToPackageCenter" class="activate-btn">
            立即开通会员
          </button>
        </div>
      </div>

      <!-- 加量包状态 -->
      <div class="packages-card">
        <div class="card-header">
          <h3>
            <i class="fas fa-battery-three-quarters"></i>
            加量包
          </h3>
          <button @click="goToPackageCenter" class="more-btn">
            更多
          </button>
        </div>
        
        <div v-if="packageList.length > 0" class="packages-list">
          <div 
            v-for="pkg in packageList" 
            :key="pkg.id"
            class="package-item"
          >
            <div class="package-info">
              <h4>{{ pkg.package_name }}</h4>
              <p>剩余 {{ pkg.remaining_calls }} 次</p>
            </div>
            <div class="package-expiry">
              <span>{{ formatDate(pkg.expires_at) }}</span>
            </div>
          </div>
        </div>
        
        <div v-else class="no-packages">
          <p>暂无加量包</p>
          <button @click="goToPackageCenter" class="buy-btn">
            购买加量包
          </button>
        </div>
      </div>

      <!-- 功能菜单 -->
      <div class="menu-section">
        <div class="menu-item" @click="openMyVideos">
          <div class="menu-icon">
            <i class="fas fa-video"></i>
          </div>
          <div class="menu-content">
            <span>我的视频</span>
            <i class="fas fa-chevron-right"></i>
          </div>
        </div>
        
        <div class="menu-item" @click="openUsageHistory">
          <div class="menu-icon">
            <i class="fas fa-history"></i>
          </div>
          <div class="menu-content">
            <span>使用记录</span>
            <i class="fas fa-chevron-right"></i>
          </div>
        </div>
        
        <div class="menu-item" @click="openBillingHistory">
          <div class="menu-icon">
            <i class="fas fa-receipt"></i>
          </div>
          <div class="menu-content">
            <span>账单记录</span>
            <i class="fas fa-chevron-right"></i>
          </div>
        </div>
      </div>

      <!-- 退出登录按钮 -->
      <div class="logout-section">
        <button @click="handleLogout" class="logout-btn">
          <i class="fas fa-sign-out-alt"></i>
          退出登录
        </button>
      </div>
    </main>

    <!-- 编辑资料弹窗 -->
    <div v-if="showEditProfile" class="modal-overlay" @click="showEditProfile = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>编辑个人资料</h3>
          <button @click="showEditProfile = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="updateProfile" class="edit-form">
            <!-- 头像上传 -->
            <div class="form-group">
              <label>头像</label>
              <div class="avatar-upload">
                <img 
                  :src="editForm.avatar_url || userInfo.avatar_url || 'https://picsum.photos/120/120?random=1'"
                  alt="头像预览"
                  class="avatar-preview"
                />
                <input 
                  type="file"
                  accept="image/*"
                  @change="handleAvatarUpload"
                  class="avatar-input"
                  :disabled="uploadingAvatar"
                />
                <div v-if="uploadingAvatar" class="upload-loading">
                  <i class="fas fa-spinner fa-spin"></i>
                </div>
                <div class="upload-btn">
                  <i class="fas fa-camera"></i>
                </div>
              </div>
            </div>
            
            <!-- 用户名 -->
            <div class="form-group">
              <label>用户名</label>
              <input 
                v-model="editForm.username"
                type="text" 
                placeholder="请输入用户名"
                required
              />
            </div>
            
            <!-- 公司名称 -->
            <div class="form-group">
              <label>公司名称</label>
              <input 
                v-model="editForm.company_name"
                type="text" 
                placeholder="请输入公司名称（可选）"
              />
            </div>
            
            <!-- 邮箱 -->
            <div class="form-group">
              <label>邮箱</label>
              <input 
                v-model="editForm.email"
                type="email" 
                placeholder="请输入邮箱"
                required
              />
            </div>
            
            <!-- 提交按钮 -->
            <div class="form-actions">
              <button type="button" @click="showEditProfile = false" class="cancel-btn">
                取消
              </button>
              <button type="submit" :disabled="updating" class="save-btn">
                {{ updating ? '保存中...' : '保存' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- 设置弹窗 -->
    <div v-if="showSettings" class="modal-overlay" @click="showSettings = false">
      <div class="modal-content settings-modal" @click.stop>
        <div class="modal-header">
          <h3>设置</h3>
          <button @click="showSettings = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="settings-list">
            <div class="setting-item">
              <div class="setting-info">
                <i class="fas fa-bell"></i>
                <span>消息通知</span>
              </div>
              <div class="setting-control">
                <label class="switch">
                  <input type="checkbox" v-model="settings.notifications">
                  <span class="slider"></span>
                </label>
              </div>
            </div>
            
            <div class="setting-item">
              <div class="setting-info">
                <i class="fas fa-moon"></i>
                <span>深色模式</span>
              </div>
              <div class="setting-control">
                <label class="switch">
                  <input type="checkbox" v-model="settings.darkMode">
                  <span class="slider"></span>
                </label>
              </div>
            </div>
            
            <div class="setting-item">
              <div class="setting-info">
                <i class="fas fa-download"></i>
                <span>自动下载</span>
              </div>
              <div class="setting-control">
                <label class="switch">
                  <input type="checkbox" v-model="settings.autoDownload">
                  <span class="slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from '@/utils/router.js';
import { useUserStoreHook } from '@/store/modules/user';
import { ElMessage, showConfirm as ElMessageBox } from '@/utils/message.js';
import ActionAPI from '@/api/comfyui-action';
import type { ActionUser, ActionUserMembership, ActionUserPackage } from '@/api/comfyui-action/types';
import { uploadFiles } from '@/api/file';
import { useTheme } from '@/composables/useTheme';

const router = useRouter();
const userStore = useUserStoreHook();

// 主题管理
const { isDark, loadTheme, toggleTheme } = useTheme();

// 状态管理
const showEditProfile = ref(false);
const showSettings = ref(false);
const updating = ref(false);
const uploadingAvatar = ref(false);
const refreshing = ref(false);

// 数据状态
const userInfo = ref<ActionUser>({} as ActionUser);
const membershipInfo = ref<ActionUserMembership | null>(null);
const packageList = ref<ActionUserPackage[]>([]);

// 编辑表单
const editForm = ref({
  username: '',
  company_name: '',
  email: '',
  avatar_url: ''
});

// 设置选项
const settings = reactive({
  notifications: true,
  darkMode: false,
  autoDownload: false
});

// 格式化日期
const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('zh-CN');
};

// 获取剩余次数（使用新的累加逻辑）
const getRemainingCalls = (membership: any) => {
  // 优先使用 today_remaining_calls，如果没有则使用 remaining_calls，最后回退到旧逻辑
  if (membership.today_remaining_calls !== undefined) {
    return Math.max(0, membership.today_remaining_calls);
  }
  if (membership.remaining_calls !== undefined) {
    return Math.max(0, membership.remaining_calls);
  }
  // 旧逻辑：daily_limit - today_calls
  return Math.max(0, (membership.daily_limit || 0) - (membership.today_calls || 0));
};

// 获取用户ID
const getUserId = (): number | null => {
  const userId = userStore.userInfo?.id;
  return userId ? Number(userId) : null;
};

// 获取用户信息
const fetchUserInfo = async () => {
  try {
    const userId = getUserId();
    if (!userId) return;
    
    const response = await ActionAPI.getUserInfo(userId);
    
    if (response.success) {
      userInfo.value = response.data;
      
      // 初始化编辑表单
      editForm.value = {
        username: response.data.username,
        company_name: response.data.company_name,
        email: response.data.email,
        avatar_url: response.data.avatar_url || ''
      };
    } else {
      // 已禁用错误提示
      console.log(response.error || '获取用户信息失败');
    }
  } catch (error) {
    console.error('获取用户信息失败:', error);
    // 已禁用错误提示
    console.log('获取用户信息失败');
  }
};

// 获取会员信息
const fetchMembershipInfo = async () => {
  try {
    const userId = getUserId();
    if (!userId) return;
    
    const response = await ActionAPI.getUserMemberships(userId, 'active');
    
    if (response.success && response.data.user_memberships && response.data.user_memberships.length > 0) {
      membershipInfo.value = response.data.user_memberships[0];
    }
  } catch (error) {
    console.error('获取会员信息失败:', error);
  }
};

// 刷新剩余次数
const handleRefreshRemaining = async () => {
  try {
    const userId = getUserId();
    if (!userId) {
      ElMessage.error('用户ID不存在');
      return;
    }

    refreshing.value = true;
    const response = await ActionAPI.refreshRemainingCalls(userId);
    
    if (response.success) {
      // 刷新成功后，更新会员信息
      // 如果返回了新的会员数据，使用返回的数据更新
      if (response.data && response.data.memberships && response.data.memberships.length > 0) {
        // 找到当前会员并更新
        const currentMembershipName = membershipInfo.value?.membership_name;
        const updatedMembership = response.data.memberships.find(
          (m: any) => m.membership_name === currentMembershipName
        );
        
        if (updatedMembership && membershipInfo.value) {
          // 更新剩余次数相关字段
          membershipInfo.value.today_remaining_calls = updatedMembership.today_remaining_calls;
          membershipInfo.value.remaining_calls = updatedMembership.today_remaining_calls;
          // 更新最后刷新时间（使用当前日期）
          membershipInfo.value.remaining_calls_last_refresh = new Date().toISOString().split('T')[0];
        }
      }
      
      // 重新获取会员信息以确保数据完全同步
      await fetchMembershipInfo();
      
      ElMessage.success(response.message || '额度刷新成功');
    } else {
      // 处理错误情况
      let errorMsg = '刷新失败';
      if (response.code === 400) {
        errorMsg = response.error || '用户ID不能为空';
      } else if (response.code === 404) {
        errorMsg = '用户不存在';
      } else if (response.code === 40005) {
        errorMsg = '账号已暂停';
      } else {
        errorMsg = response.error || response.message || '刷新失败，请稍后重试';
      }
      ElMessage.error(errorMsg);
    }
  } catch (error: any) {
    console.error('刷新剩余次数失败:', error);
    ElMessage.error(error.message || '刷新失败，请稍后重试');
  } finally {
    refreshing.value = false;
  }
};

// 获取加量包信息
const fetchPackageInfo = async () => {
  try {
    const userId = getUserId();
    if (!userId) return;
    
    const response = await ActionAPI.getUserPackages(userId, 'available');
    
    if (response.success && response.data.user_packages) {
      packageList.value = response.data.user_packages;
    }
  } catch (error) {
    console.error('获取加量包信息失败:', error);
  }
};

// 头像上传处理
const handleAvatarUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  
  if (!file) return;
  
  // 验证文件类型
  if (!file.type.startsWith('image/')) {
    // 已禁用错误提示
    console.log('请选择图片文件');
    return;
  }
  
  // 验证文件大小（2MB）
  if (file.size > 2 * 1024 * 1024) {
    // 已禁用错误提示
    console.log('图片大小不能超过2MB');
    return;
  }
  
  try {
    uploadingAvatar.value = true;
    
    const userId = getUserId();
    const response = await uploadFiles(file, `avatar_${userId}_${Date.now()}`);
    
    if ((response.data as any)?.file_url) {
      editForm.value.avatar_url = (response.data as any).file_url;
      // 已禁用成功提示
      console.log('头像上传成功');
    } else {
      throw new Error('上传响应无效');
    }
  } catch (error) {
    console.error('头像上传失败:', error);
    // 已禁用错误提示
    console.log('头像上传失败，请重试');
  } finally {
    uploadingAvatar.value = false;
    target.value = '';
  }
};

// 更新个人资料
const updateProfile = async () => {
  try {
    updating.value = true;
    const userId = getUserId();
    if (!userId) return;
    
    const response = await ActionAPI.updateUserInfo(userId, editForm.value);
    
    if (response.success) {
      userInfo.value = { ...userInfo.value, ...response.data };
      
      // 更新用户store中的信息
      userStore.userInfo = {
        ...userStore.userInfo,
        username: response.data.username,
        email: response.data.email,
        company_name: response.data.company_name,
        avatar_url: response.data.avatar_url
      };
      
      showEditProfile.value = false;
      // 已禁用成功提示
      console.log('个人资料更新成功');
    }
  } catch (error) {
    console.error('更新个人资料失败:', error);
    // 已禁用错误提示
    console.log('更新个人资料失败');
  } finally {
    updating.value = false;
  }
};

// 导航方法
const goBack = () => {
  router.back();
};

const goToPackageCenter = () => {
  router.push('/pages/mobile/package-center');
};

const openSettings = () => {
  showSettings.value = true;
};

const openMyVideos = () => {
  // 已禁用提示信息
  console.log('我的视频功能开发中');
};

const openUsageHistory = () => {
  // 已禁用提示信息
  console.log('使用记录功能开发中');
};

const openBillingHistory = () => {
  // 已禁用提示信息
  console.log('账单记录功能开发中');
};

// 退出登录
const handleLogout = () => {
  // 已禁用确认对话框，直接执行退出操作
  console.log('Logout confirmation bypassed');
  try {
    // 清除所有存储的用户信息
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('userId');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('userInfo');
    sessionStorage.removeItem('userId');
    
    // 清除store中的用户信息
    if (userStore && typeof userStore.logout === 'function') {
      userStore.logout();
    } else {
      userStore.userId = '';
      userStore.nickname = '';
      userStore.avatar = '';
      userStore.token = '';
    }
    
    // 已禁用成功提示
    console.log('已退出登录');
    router.push('/pages/mobile/login');
  } catch (error) {
    console.error('退出登录失败:', error);
    // 已禁用成功提示
    console.log('已退出登录');
    router.push('/pages/mobile/login');
  }
};

// 页面加载时获取数据
onMounted(async () => {
  // 加载主题设置
  loadTheme();
  
  // 等待用户状态初始化完成
  await new Promise<void>(resolve => {
    const checkUserState = () => {
      if (userStore.token || localStorage.getItem('token')) {
        resolve();
      } else {
        // 等待50ms后再次检查，最多等待2秒
        setTimeout(() => {
          if (userStore.token || localStorage.getItem('token')) {
            resolve();
          } else if (Date.now() - startTime > 2000) {
            resolve(); // 超时也继续执行
          } else {
            checkUserState();
          }
        }, 50);
      }
    };
    const startTime = Date.now();
    checkUserState();
  });
  
  if (!userStore.token) {
    // 已禁用警告提示
    console.log('请先登录');
    router.push('/pages/mobile/login?redirect=' + encodeURIComponent(router.currentRoute.value.fullPath));
    return;
  }
  
  fetchUserInfo();
  fetchMembershipInfo();
  fetchPackageInfo();
});
</script>

<style scoped lang="scss">
.mobile-user-center {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  background-attachment: fixed; /* 确保背景固定 */
  display: flex;
  flex-direction: column;
  position: relative;
}

/* 为了确保背景色覆盖整个页面高度 */
.mobile-user-center::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  z-index: -1;
}

.mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  position: sticky;
  top: 0;
  z-index: 100;
  
  .back-btn, .settings-btn {
    width: 40px;
    height: 40px;
    border: none;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    color: white;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
  
  h1 {
    color: white;
    font-size: 20px;
    font-weight: 600;
    margin: 0;
  }
}

.main-content {
  flex: 1;
  padding: 20px 16px;
  position: relative;
  z-index: 1; /* 确保内容在背景之上 */
}

.user-info-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 20px;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  
  .user-avatar {
    position: relative;
    display: inline-block;
    margin-bottom: 16px;
    
    img {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      object-fit: cover;
      border: 4px solid #fff;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
      cursor: pointer;
    }
    
    .avatar-edit-btn {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 28px;
      height: 28px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
      
      i {
        color: white;
        font-size: 12px;
      }
    }
  }
  
  .user-details {
    h2 {
      color: #333;
      font-size: 24px;
      font-weight: bold;
      margin: 0 0 8px 0;
    }
    
    p {
      color: #666;
      font-size: 16px;
      margin: 0 0 16px 0;
    }
    
    .user-stats {
      display: flex;
      flex-direction: column;
      gap: 8px;
      
      .stat-item {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: #666;
        font-size: 14px;
        
        i {
          color: #667eea;
          width: 16px;
        }
      }
    }
  }
}

.membership-card, .packages-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 20px;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    
    h3 {
      color: #333;
      font-size: 18px;
      font-weight: bold;
      margin: 0;
      flex: 1;
      display: flex;
      align-items: center;
      gap: 8px;
      
      i {
        color: #ffd700;
      }
    }
    
    .header-actions {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .upgrade-btn, .more-btn, .refresh-btn {
      padding: 6px 12px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: white;
      border: none;
      border-radius: 16px;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: 4px;
      white-space: nowrap;
      
      &:hover:not(:disabled) {
        transform: translateY(-1px);
      }
      
      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      
      i {
        font-size: 12px;
        
        &.fa-spin {
          animation: spin 1s linear infinite;
        }
      }
    }
    
    .refresh-btn {
      background: linear-gradient(135deg, #10b981, #059669);
      
      &:hover:not(:disabled) {
        background: linear-gradient(135deg, #059669, #047857);
      }
    }
  }
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}

.membership-info {
  .membership-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(135deg, #ffd700, #ffb347);
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    font-weight: bold;
    margin-bottom: 16px;
    
    i {
      font-size: 16px;
    }
  }
  
  .membership-details {
    margin-bottom: 16px;
    
    .detail-row {
      display: flex;
      justify-content: between;
      margin-bottom: 8px;
      
      .label {
        color: #666;
        font-size: 14px;
      }
      
      .value {
        color: #333;
        font-size: 14px;
        font-weight: 600;
      }
    }
  }
  
  .usage-progress {
    .progress-info {
      display: flex;
      justify-content: between;
      margin-bottom: 8px;
      font-size: 14px;
      
      span:first-child {
        color: #666;
      }
      
      span:last-child {
        color: #333;
        font-weight: 600;
      }
    }
    
    .progress-bar {
      height: 8px;
      background: #e9ecef;
      border-radius: 4px;
      overflow: hidden;
      
      .progress-fill {
        height: 100%;
        background: linear-gradient(135deg, #667eea, #764ba2);
        transition: width 0.3s ease;
      }
    }
  }
}

.no-membership, .no-packages {
  text-align: center;
  padding: 20px;
  
  .empty-icon {
    font-size: 48px;
    color: #ccc;
    margin-bottom: 16px;
  }
  
  p {
    color: #666;
    font-size: 16px;
    margin: 0 0 16px 0;
  }
  
  .activate-btn, .buy-btn {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 12px;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
}

.packages-list {
  .package-item {
    display: flex;
    align-items: center;
    justify-content: between;
    padding: 12px 0;
    border-bottom: 1px solid #e9ecef;
    
    &:last-child {
      border-bottom: none;
    }
    
    .package-info {
      flex: 1;
      
      h4 {
        color: #333;
        font-size: 16px;
        font-weight: 600;
        margin: 0 0 4px 0;
      }
      
      p {
        color: #10b981;
        font-size: 14px;
        margin: 0;
      }
    }
    
    .package-expiry {
      color: #666;
      font-size: 12px;
    }
  }
}

.menu-section {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 8px;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  .menu-item {
    display: flex;
    align-items: center;
    padding: 16px;
    cursor: pointer;
    border-radius: 12px;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(102, 126, 234, 0.1);
    }
    
    .menu-icon {
      width: 40px;
      height: 40px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 16px;
      
      i {
        color: white;
        font-size: 18px;
      }
    }
    
    .menus-content {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: between;
      
      span {
        color: #333;
        font-size: 16px;
        font-weight: 500;
      }
      
      i {
        color: #999;
        font-size: 14px;
      }
    }
  }
}

.logout-section {
  .logout-btn {
    width: 100%;
    background: #dc3545;
    color: white;
    border: none;
    padding: 16px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.3s ease;
    
    &:hover {
      background: #c82333;
      transform: translateY(-2px);
    }
  }
}

// 弹窗样式
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 400px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  &.settings-modal {
    max-height: 60vh;
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: between;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  
  h3 {
    color: #333;
    font-size: 20px;
    margin: 0;
    flex: 1;
  }
  
  .close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: #f8f9fa;
    border-radius: 50%;
    color: #666;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: #e9ecef;
    }
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.edit-form {
  .form-group {
    margin-bottom: 20px;
    
    label {
      display: block;
      color: #333;
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 8px;
    }
    
    input {
      width: 100%;
      height: 48px;
      background: #f8f9fa;
      border: 2px solid #e9ecef;
      border-radius: 8px;
      padding: 0 16px;
      font-size: 16px;
      color: #333;
      
      &:focus {
        outline: none;
        border-color: #667eea;
      }
    }
  }
  
  .avatar-upload {
    position: relative;
    display: inline-block;
    
    .avatar-preview {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      object-fit: cover;
      border: 4px solid #f8f9fa;
    }
    
    .avatar-input {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }
    
    .upload-loading {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      
      i {
        color: white;
        font-size: 20px;
      }
    }
    
    .upload-btn {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 24px;
      height: 24px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      
      i {
        color: white;
        font-size: 12px;
      }
    }
  }
  
  .form-actions {
    display: flex;
    gap: 12px;
    
    button {
      flex: 1;
      height: 48px;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &.cancel-btn {
        background: #f8f9fa;
        color: #666;
        border: 2px solid #e9ecef;
        
        &:hover {
          background: #e9ecef;
        }
      }
      
      &.save-btn {
        background: linear-gradient(135deg, #667eea, #764ba2);
        color: white;
        
        &:hover:not(:disabled) {
          transform: translateY(-2px);
        }
        
        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
    }
  }
}

.settings-list {
  .setting-item {
    display: flex;
    align-items: center;
    justify-content: between;
    padding: 16px 0;
    border-bottom: 1px solid #e9ecef;
    
    &:last-child {
      border-bottom: none;
    }
    
    .setting-info {
      display: flex;
      align-items: center;
      gap: 12px;
      
      i {
        width: 20px;
        color: #667eea;
        font-size: 18px;
      }
      
      span {
        color: #333;
        font-size: 16px;
      }
    }
    
    .setting-control {
      .switch {
        position: relative;
        display: inline-block;
        width: 50px;
        height: 28px;
        
        input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        
        .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: .4s;
          border-radius: 28px;
          
          &:before {
            position: absolute;
            content: "";
            height: 20px;
            width: 20px;
            left: 4px;
            bottom: 4px;
            background-color: white;
            transition: .4s;
            border-radius: 50%;
          }
        }
        
        input:checked + .slider {
          background: linear-gradient(135deg, #667eea, #764ba2);
          
          &:before {
            transform: translateX(22px);
          }
        }
      }
    }
  }
}

/* 修复菜单项的样式问题 */
.menu-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  span {
    color: #333;
    font-size: 16px;
    font-weight: 500;
  }
  
  i {
    color: #999;
    font-size: 14px;
  }
}

/* 响应式设计 */
@media (max-width: 480px) {
  .main-content {
    padding: 16px 12px;
  }
  
  .user-info-card, .membership-card, .packages-card, .menu-section {
    padding: 16px;
  }
}

/* 动画效果 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-info-card, .membership-card, .packages-card, .menu-section {
  animation: fadeInUp 0.5s ease-out;
}

.membership-card { animation-delay: 0.1s; }
.packages-card { animation-delay: 0.2s; }
.menu-section { animation-delay: 0.3s; }
.logout-section { animation-delay: 0.4s; }

// 主题切换按钮样式
.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.theme-toggle {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }

  i {
    font-size: 16px;
    color: white;
    transition: all 0.3s ease;
  }
}

// 暗色主题样式
.mobile-user-center.dark-theme {
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  background-attachment: fixed; /* 确保背景固定 */
}

/* 为暗色主题确保背景色覆盖整个页面高度 */
.mobile-user-center.dark-theme::before {
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
}

.mobile-user-center.dark-theme .mobile-header {
  background: rgba(0, 0, 0, 0.4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  h1 {
    color: #ffffff;
  }

  .back-btn, .settings-btn {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  .theme-toggle {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
}

.mobile-user-center.dark-theme .main-content {
    .user-info-card, .membership-card, .packages-card, .menu-section {
      background: rgba(30, 30, 50, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.1);

      h2, h3 {
        color: #ffffff;
      }

      p {
        color: #ffffff;
      }
    }

    .user-info-card {
      .user-details h3 {
        color: #ffffff;
      }

      .user-stats {
        .stat-item {
          .stat-value {
            color: #ffffff;
          }
          .stat-label {
            color: #ffffff;
          }
        }
      }

      .edit-profile-btn {
        background: linear-gradient(135deg, #2d3748, #4a5568);
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: #e2e8f0;
        
        &:hover {
          background: linear-gradient(135deg, #4a5568, #2d3748);
          transform: translateY(-2px);
        }
      }
    }

    .membership-info, .package-info {
      .info-item {
        background: rgba(45, 55, 72, 0.5);
        border: 1px solid rgba(255, 255, 255, 0.1);

        .info-label {
          color: #ffffff;
        }

        .info-value {
          color: #ffffff;
        }
      }
    }

    .menu-items {
      .menu-item {
        background: rgba(30, 30, 50, 0.6);
        border: 1px solid rgba(255, 255, 255, 0.1);
        color: #ffffff;

        &:hover {
          background: rgba(45, 55, 72, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }

        .menu-icon {
          color: #4a90e2;
        }
      }
    }

    .logout-btn {
      background: linear-gradient(135deg, #e53e3e, #c53030);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: #ffffff;

      &:hover {
        background: linear-gradient(135deg, #c53030, #e53e3e);
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(197, 48, 48, 0.4);
      }
    }

    // 覆盖所有文字颜色为白色
    h1, h2, h3, h4, h5, h6,
    p, span, div, li, label,
    .user-name, .user-email, .stat-value, .stat-label,
    .info-label, .info-value, .menu-text,
    .form-label, .form-input, .modal-body p,
    .history-item h4, .history-item p {
      color: #ffffff !important;
    }

    // 特殊颜色保持不变
    .status.active { color: #10b981 !important; }
    .status.expired { color: #f56565 !important; }
    .price-amount { color: #4a90e2 !important; }
  }

  // 弹窗样式
  .edit-modal, .settings-modal {
    .modal-content {
      background: rgba(30, 30, 50, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.1);

      .modal-header {
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);

        h3 {
          color: #ffffff;
        }

        .close-btn {
          background: rgba(255, 255, 255, 0.1);
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.2);

          &:hover {
            background: rgba(255, 255, 255, 0.2);
          }
        }
      }

      .form-group {
        .form-label {
          color: #ffffff;
        }

        .form-input {
          background: rgba(45, 55, 72, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #ffffff;

          &::placeholder {
            color: rgba(255, 255, 255, 0.5);
          }

          &:focus {
            border: 1px solid #4a90e2;
            box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
          }
        }
      }

      .save-btn, .cancel-btn {
        background: linear-gradient(135deg, #2d3748, #4a5568);
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: #e2e8f0;

        &:hover {
          background: linear-gradient(135deg, #4a5568, #2d3748);
          transform: translateY(-2px);
        }
      }

      .cancel-btn {
        background: rgba(255, 255, 255, 0.1);
        
        &:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      }
    }
  }
</style>
